<template>
    <div class="of-product-preview">
        <ScrollAreaRoot type="auto" class="of-product-scroll-area">
        <ScrollAreaViewport ref="viewport" class="of-product-viewport" :style="{ maxHeight }"
            :tabindex="limitHeight && expanded && items.length > 10 ? 0 : undefined"
            :aria-label="limitHeight && expanded && items.length > 10 ? 'Products' : undefined">
            <div ref="content">
                <slot :visible-items="expanded ? items : items.slice(0, 6)" />
            </div>
        </ScrollAreaViewport>
        <ScrollAreaScrollbar orientation="vertical" class="of-product-scrollbar">
            <ScrollAreaThumb class="of-product-scroll-thumb" />
        </ScrollAreaScrollbar>
        </ScrollAreaRoot>
        <button v-if="items.length > 6" type="button" class="of-product-toggle"
            :aria-expanded="expanded" @click.stop="toggle">
            {{ expanded ? 'Show Less' : `Show ${items.length - 6} More` }}
            <ChevronRightIcon class="w-3.5 h-3.5" :class="expanded ? '-rotate-90' : 'rotate-90'" />
        </button>
    </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { ScrollAreaRoot, ScrollAreaViewport, ScrollAreaScrollbar, ScrollAreaThumb } from 'radix-vue';
import { ChevronRightIcon } from '@heroicons/vue/solid';

const props = defineProps({
    items: { type: Array, default: () => [] },
    rowSelector: { type: String, default: '.of-item-row' },
    limitHeight: { type: Boolean, default: true },
});
const expanded = ref(false);
const viewport = ref(null);
const content = ref(null);
const maxHeight = ref(undefined);
let observer;

// Measure actual rows so wrapped product details and responsive sizes still fit ten products.
const measure = () => {
    const rows = content.value?.querySelectorAll(props.rowSelector);
    if (!props.limitHeight || !expanded.value || !rows || rows.length <= 10) {
        maxHeight.value = undefined;
        return;
    }
    const height = rows[9].getBoundingClientRect().bottom - content.value.getBoundingClientRect().top;
    maxHeight.value = height > 0 ? `${height}px` : undefined;
};
const toggle = () => {
    expanded.value = !expanded.value;
    if (viewport.value?.viewportElement) viewport.value.viewportElement.scrollTop = 0;
};
watch(() => props.items, () => {
    expanded.value = false;
    if (viewport.value?.viewportElement) viewport.value.viewportElement.scrollTop = 0;
});
watch([expanded, () => props.items, () => props.limitHeight], async () => {
    await nextTick();
    measure();
});
onMounted(() => {
    observer = new ResizeObserver(measure);
    observer.observe(content.value);
    measure();
});
onBeforeUnmount(() => observer?.disconnect());
</script>

<style scoped>
.of-product-scroll-area {
    position: relative;
    overflow: hidden;
}
.of-product-viewport {
    width: 100%;
    overscroll-behavior-y: contain;
}
.of-product-scrollbar {
    display: flex;
    width: 10px;
    padding: 2px;
    user-select: none;
    touch-action: none;
    background: #f3f4f6;
}
.of-product-scroll-thumb {
    position: relative;
    flex: 1;
    min-height: 32px;
    border-radius: 999px;
    background: #9ca3af;
}
.of-product-scroll-thumb:hover { background: #6b7280; }
.of-product-toggle {
    appearance: none;
    border: none;
    background: transparent;
    color: #8f6f4f;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 10px 12px;
    padding: 0;
    font-size: 12px;
    font-weight: 500;
}
.of-product-toggle:hover { color: #bd966f; }
.of-product-toggle:focus-visible { outline: 2px solid #8f6f4f; outline-offset: 4px; }
</style>
